<template>
  <v-container fluid justify-start>

    <!-- Page Title -->
    <app-page-title v-bind:title="'Cattlytics Help'" v-bind:icon="'help'" />

    <v-expansion-panels v-model="panel">
      
      <!-- Registration -->
      <v-expansion-panel>
        <v-expansion-panel-header :class="cls_expansion_title">
          Sign In and Registration
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-alert type="info" border="info">
            Before you can access any functionality inside Cattlytics, you must be authenticated.
          </v-alert>

          <!-- Logging In -->
          <v-card class="pb-5 mb-3">
            <v-card-title class="font-weight-bold">
              Logging Into Cattlytics
            </v-card-title>
            <v-card-text>
              <v-img
                :src="ca_img_signin"
                style="max-width: 900px"
              />
              <span class="caption">Click or Tap “Sign In”</span>
            </v-card-text>

            <v-card-text class="body-1 text-xs-left pb-0">
              <v-img
                :src="ca_img_login"
                style="max-width: 300px"
              />
              <span class="caption">
                Enter your pre-registered email address and password and select <span class="font-weight-bold">LOG IN ></span>
              </span>
            </v-card-text>
          </v-card>

          <!-- Sign Up -->
          <v-card class="pb-5">
            <v-card-title class="font-weight-bold">
              Sign Up
            </v-card-title>

            <v-card-text>
              <v-img
                :src="ca_img_signup"
                style="max-width: 300px"
              />
              <span class="caption">Choose “Sign Up” to register a new email address.</span>
            </v-card-text>

            <v-card-text class="pt-2">
              <div class="body-1 font-weight-bold">Enter an email address</div>
              <span class="body-2 font-weight-medium">Note:</span>
              <ul>
                <li>Does not have to be a live email address</li>
                <li>Must be formatted correctly without spaces</li>
                <li>A verification email will be sent to this address</li>
                <li>If the address is not real that is OK, as long as it is unique in Cattlytics</li>                    
              </ul>
            </v-card-text>

            <v-card-text class="pt-0">
              <v-img
                :src="ca_img_password"
                style="max-width: 300px"
              />
              <span class="caption">
                Choose a password then click <span class="font-weight-bold">SIGN UP ></span>
              </span>
              <div class="caption font-italic font-weight-medium">Passwords must be at least 8 characters long</div>
            </v-card-text>

          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Launching Bull Finder -->
      <v-expansion-panel>
        <v-expansion-panel-header :class="cls_expansion_title">
          Launching Bull Finder
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card class="pb-3 mb-4">
            <v-card-title class="font-weight-bold pb-1">
              3 Ways to Launch Bull Finder
            </v-card-title>

            <v-card-text>
              <v-list dense class="pa-0">
                <v-list-item class="body-1 pb-0">
                  <v-list-item-icon>
                    <v-badge content="1" inline></v-badge>
                  </v-list-item-icon>
                  <v-list-item-content>From the menu</v-list-item-content>
                </v-list-item>
                <v-list-item class="body-1">
                  <v-list-item-icon>
                    <v-badge content="2" inline></v-badge>
                  </v-list-item-icon>
                  <v-list-item-content>Button on the home page</v-list-item-content>
                </v-list-item>
                <v-list-item class="body-1">
                  <v-list-item-icon>
                    <v-badge content="3" inline></v-badge>
                  </v-list-item-icon>
                  <v-list-item-content>List of registered sales on the home page</v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>

            <v-divider class="mb-5" />

            <v-card-text class="body-1 font-weight-medium py-0">
              1. Launching Bull Finder from Cattlytics menu (typical)
            </v-card-text>

            <v-card-text class="body-1">
              <v-img
                :src="bf_img_home_1"
                style="max-width: 900px"
              />
              <span class="caption">Open the menu drawer (click top left icon)</span>
            </v-card-text>

            <v-card-text class="body-1">
              <v-img
                :src="bf_img_home_2"
                style="max-width: 900px"
              />
              <span class="caption">Select the menu option named <i>Bull Finder</i></span>
            </v-card-text>

            <v-divider class="mb-5" />

            <v-card-text class="body-1 font-weight-medium py-0">
              2. Big button in the middle of the home page
            </v-card-text>

            <v-card-text class="body-1">
              <v-img
                :src="bf_img_home_launch_bullfinder"
                style="max-width: 900px"
              />
              <span class="caption">Click "Launch Bull Finder"</span>
            </v-card-text>

            <v-divider class="mb-5" />

            <v-card-text class="body-1 font-weight-medium py-0">
              3. Launching Bull Finder from the registered sales list (when in Sale Day Mode)
            </v-card-text>

            <v-card-text class="body-2">
              When Bull Finder is flipped into "Sale Day Mode" its behaviour is optimized to enable users to quickly select bulls for a sale.
              <div class="body-2 pt-2">
                <span class="font-weight-bold font-italic">In Sale Day Mode:</span>
                <ul>
                  <li>A list of registered sales is displayed on the home page</li>
                  <li>When you choose a sale, Bull Finder will quickly show the queries that are based on the source of that sale</li>
                  <li>If you do not have a query defined, a set of default queries is automatically created for you</li>
                </ul> 
              </div>
            </v-card-text>

            <v-card-text class="pt-1">
              <v-img
                :src="bf_img_home_3"
                style="max-width: 900px"
              />
              <span class="caption">Select the sale you are interested in by clicking or tapping it</span>
            </v-card-text> 

          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Operating Bull Finder -->
      <v-expansion-panel>
        <v-expansion-panel-header :class="cls_expansion_title">
          Operating Bull Finder
        </v-expansion-panel-header>
        <v-expansion-panel-content>

          <!-- Intro -->
          <v-card>
            <v-card-text class="body-1 pt-0">
              As soon as it is launched Bull Finder displays 2 panels - one for <i>Queries</i> and the other for <i>Results</i>.  In the example below the queries panel shows 2 queries, one named "Heifer Bulls" and the second one named "Maternal".
            </v-card-text>
            <v-card-text class="body-1 pt-0">
              The first query in the list is always <i>activated</i> by default.
              Once the Heifer Bull query has been activated, the database source is searched and matching bulls appear in the results panel.
            </v-card-text>
            <v-card-text class="pt-1">
            <v-img
              :src="bf_img_query_panels"
              style="max-width: 900px"
              />
              <span class="caption">When the Heifer Bull query is <i>activated</i>, matching bulls are in the results panel sorted by Rank.</span>
            </v-card-text>

            <!-- What Is A Query? -->
            <v-card-title class="headline pb-0">
              What Is A Query?
            </v-card-title>
            <v-card-text class="body-1 py-2">
              <ul>
                <li>A query is a <b>prioritized set of traits</b> or <i>criteria</i>
                <li>The bull source, breed and year of birth are built into the definition and are fixed</li>
                <li>Traits are selected when the query is created or later modified</li>
                <li>Launch the <i>Bull Finder Query Manager</i> to create or change a query</li>
                <li>Between 1 and 6 traits per query</li>
              </ul>
            </v-card-text>

            <v-card-text class="body-1 pb-0">
              <span class="font-weight-medium">Examples of Traits or Criteria</span>
              <ul>
                <li>Birth Weight EBV</li>
                <li>Adjusted Scrotal Circumferance</li>
                <li>Average Daily Gain Wean to Year</li>
                <li>Age of Dam</li>
                <li>Calving Ease</li>
                <li>Hide Colour</li>
              </ul> 
            </v-card-text>

            <v-divider class="my-5" />

            <v-card-title class="pt-0 pb-2">
              Active Query
            </v-card-title>

            <v-card-text class="body-1 pb-0">  
              <span>Activate (click or tap) the second query <i>(Maternal)</i> and the bulls in the results panel are replaced with matches for traits defined for the Maternal query.</span>
              <v-img
                :src="bf_img_query_panels_maternal"
                style="max-width: 900px"
                />
              <span class="caption">Results are refreshed for the second <i>(Maternal)</i> query.</span>
            </v-card-text>

            <v-divider class="my-5" />

            <v-card-title class="headline py-0">
              Queries Panel
            </v-card-title>
            <v-card-text class="body-1 text-xs-left pb-0">
              The queries panel contains all of your personal queries in the form of cards.
            </v-card-text>
            <v-card-text class="body-1 text-xs-left pb-0">
            <div class="body-1 text-xs-left mt-2">
              <ul>
                <li>Breed: displayed only for sources that can have more than 1 breed</li>
                <li>Title: The name of the query</li>
                <li>Primary Trait: The number one trait for you.</li>
                <li>Year of Birth: Bull Finder stores several years worth of bulls</li>
                <li>Description: A longer description of the query and what it is attempting to solve</li>
                <li>Source: The source of the bulls. Bull Finder supports many bull sources. Each source may have a different set of traits.  Each source has its own colour.  In the previous example Beefbooster sourced queries are always green.</li>
              </ul> 
            </div>
            </v-card-text>
            <v-img
              :src="bf_img_query_card"
              style="max-width: 800px"
              class="ml-3"
            />

            <v-card-title class="headline pb-0">
             Activating a Query
            </v-card-title>
            <v-card-text class="body-1 text-xs-left pb-0">
             To activate a query, click or tap the card representing it. Cattlytics will search the source bull database, then filter, rank and display the matches in the results panel.
            </v-card-text>

            <v-card-title class="headline pb-0">
             Results Panel
            </v-card-title>
            <v-card-text class="body-1 text-xs-left pb-0">
             The results panel shows all bulls that match the criteria defined in the currently active query.
            </v-card-text>
            <v-card-text class="body-1 text-xs-left pb-0">
              <div class="body-1 text-xs-left mt-2">
                <ul>
                  <li>Title: The name of the currently active query</li>
                  <li>Change trait limits: Clicking the gear icon will launch a dialog that allows you to personalize the currently active query</li>
                  <li>Export results: Clicking the downarrow icon will launch a dialog that lets you export query results to Excel, CSV or plain text</li>
                  <li>Filter: limits the results table by matching text</li>
                  <li>Headings: tap a heading to change the sort order</li>
                </ul> 
              </div>
            </v-card-text>
            <v-img
              :src="bf_img_results_toolbar"
              style="max-width: 800px"
              class="ml-3"
            />
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Understanding Your Results -->
      <v-expansion-panel>       
        <v-expansion-panel-header :class="cls_expansion_title">
          Understanding The Query Results
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card>

            <v-alert type="info" class="mb-1">
              Rank is based on the number of sequenced matches
            </v-alert>

            <v-card-text class="pt-0 pb-0">
              <div class="py-2">
                <b><i>Sequenced Matches</i></b> is the count of matching traits <b>starting with the Primary Trait</b> and descending in the order of priority
              </div>

              <div class="py-2">Example - using the Heifer Bulls query</div>
              <div class="pb-2">The Heifer Bulls Query : query traits listed by priority</div>

              <v-card-text class="pt-0">
                <ol>
                  <li>Selection Index:   in the top 10th percentile</li>
                  <li>Birth Weight EBV:  in the top 25th percentile</li>
                  <li>Age of Dam:        between 5 and 10 years</li>
                  <li>Hide Colour:       Red</li>
                </ol>
              </v-card-text>

              The <b>Primary Trait</b> in this query is Selection Index (because it's the first trait listed)
              <v-img
                :src="bf_img_query_understanding_1"
                style="max-width: 800px"
              />
            </v-card-text>

            <v-card-text class="mt-3 pb-0">
               <div>Below are the matching bulls returned when the Heifer Bull query is activated.  Results are sorted by Rank.</div>
               <div>AX 40G is the top ranked bull for this particular query.</div>
               <div>Notice that small coloured indicator to the right of the rank.</div> 
               <div>Indicators can be green, yellow, red or gray.</div> 
               <div>The colour denotes the quality of match for each bull.</div>
            <v-img
              :src="bf_img_query_result"
              style="max-width: 800px"
            />
            </v-card-text>

            <v-card-title class="title pb-3">
              Rank Indicator Definitions
            </v-card-title>

            <v-card-text class="pb-1">
              <div>
                The following definitions assume that the total number of prioritized traits is <b>4</b> which our example has.
              </div>
              <div>
                All <i>non-primary</i> traits by definition have lower priorities than the primary trait.
              </div>
              <div>
                The <i>sequential match count</i> is the number of traits a bull matches in the order of prioritization, starting with the first, or primary trait.
              </div>
              <v-card-text class="pb-0">
                <div>
                  <v-icon class="ico-rank perfect-match">
                    lens
                  </v-icon>
                  Has a <i>sequential match count</i> of <b>4</b> (all traits matched)
                </div>
                <div>
                  <v-icon class="ico-rank average-match">
                    lens
                  </v-icon>
                  Has a sequential match count of <b>3</b> (out of 4)
                </div>
                <div>
                  <v-icon class="ico-rank poor-match">
                    lens
                  </v-icon>
                  Has a sequential match count of <b>1 - 2</b> (out of 4)
                </div>
                <div>
                  <v-icon class="ico-rank not-a-match">
                    lens
                  </v-icon>
                  Has a sequential match count of <b>0</b>, but matches at least 1 <i>non-primary</i> trait
                </div>

                <div class="mt-3">
                  <b>Quick Summary</b>
                </div>
                <div>
                  Green, yellow or red: the primary trait matched, plus one or more other traits.
                </div>
                <div>
                  Gray: primary trait was not matched, but one or more other traits did.
                </div>
              </v-card-text>
            </v-card-text>

            <v-card-text class="pb-1">
              <div>Click or tap the coloured indicator to open a box showing the traits and values for the particular bull.</div>
              <div>In our example, the #1 ranked bull (AX 40G) has a green indicator and the following values for each trait</div>
              <ol>
                <li>Selection Index:  109.05</li>
                <li>Birth Weight EBV: 3.98</li>
                <li>Age of Dam:       10</li>
                <li>Hide Colour:      Red</li>
              </ol>

              <div class="py-2">
                The green indicator means all traits were matched.  Opening the match details verifies this.
              </div>

              <v-img
                :src="bf_img_query_result_green"
                style="max-width: 600px"
              />
            </v-card-text>

            <v-card-text>
              <div>
                Upon examining the match details of the # 2 ranked bull (AX 25G), which has a 
                <v-icon class="co-rank average-match">
                  lens
                </v-icon>
                indicator, we see there are 3 sequenced matches as expected.  Bulls for this query with a yellow indicator will have 3 sequenced matches.
              </div>
              <v-img
                :src="bf_img_query_result_yellow"
                style="max-width: 600px"
              />  
            </v-card-text>

            <v-card-text>
              <div>
                Upon examining the match details of the # 4 ranked bull which has a 
                <v-icon class="co-rank poor-match">
                 lens
                </v-icon>
                indicator, we see there are only <b>2 sequential</b> matches but <b>3 total</b> matches.  It did not match on Dam Age.
              </div>      
              
              <v-img
                :src="bf_img_query_result_red"
                style="max-width: 600px"
              />
            </v-card-text>

            <v-card-text>
              <div>
                Upon examining the match details of the # 16 ranked bull (AX 87 G) which has a 
                <v-icon class="co-rank not-a-match">
                  lens
                </v-icon>
                indicator, we see the primary trait (Selection Index) was not matched, so this bull <b>0 sequential</b> matches and <b>2 total</b> matches.  By definition any bulls with a 
                <v-icon class="co-rank not-a-match">
                  lens
                </v-icon>
                  indicator do not match on the primary trait.
              </div>      
              <v-img
                :src="bf_img_query_result_gray"
                style="max-width: 800px"
              />  
            </v-card-text>

          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>
      
       <!-- Managing and Customizing Your Queries -->
      <v-expansion-panel>       
        <v-expansion-panel-header :class="cls_expansion_title">
          Managing and Customizing Your Queries
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card>
            <v-alert type="info">
              Use the Bull Finder Query Manager to manage all your queries
            </v-alert>

            <v-card-title>
              <span>Open <i>Bull Finder Query Manager</i></span>
            </v-card-title>

            <v-card-text>
              <v-list dense class="pa-0">
                <v-list-item class="body-1 pb-0">
                  <v-list-item-icon>
                    <v-badge content="1" inline></v-badge>
                  </v-list-item-icon>
                  <v-list-item-content>From the main menu</v-list-item-content>
                </v-list-item>
                <v-list-item class="body-1">
                  <v-list-item-icon>
                    <v-badge content="2" inline></v-badge>
                  </v-list-item-icon>
                  <v-list-item-content>Shortcut on Bull Finder</v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>

            <v-divider class="my-5" />

            <v-card-text class="body-1 font-weight-medium py-0">
              1. Open Query Manager from the Cattlytics main menu
            </v-card-text>

            <v-card-text>
              <v-img
                :src="bf_img_manage_queries_main_menu"
                style="max-width: 900px"
              />
              <span class="caption">To navigate to Query Manager from the Cattlytics main menu select "Bull Finder Queries".</span>
            </v-card-text>

            <v-divider class="my-5" />

            <v-card-text class="body-1 font-weight-medium py-0">
              2. Open Query Manager from the shortcut on Bull Finder
            </v-card-text>

            <v-card-text class="pb-0">
              <v-img
                :src="bf_img_manage_queries_button"
                style="max-width: 900px"
              />
              <span class="caption">To navigate to Query Manager directly from Bull Finder click the "Manage Queries" button.</span>
            </v-card-text>

            <v-divider class="my-5" />

            <v-card-title class="pt-0">
              Query Manager
            </v-card-title>

            <v-card-text class="body-1 py-0">
              <div>
                Use <i>Query Manager</i> to manage all your queries.
              </div>
            </v-card-text>
            
            <v-card-text class="pb-0 pt-1">
              <v-img
                :src="bf_img_manager"
                style="max-width: 1100px"
              />
              <span class="caption">Figure showing some of the features of <i>Query Manager</i>.</span>
            </v-card-text>

            <v-card-title class="pb-1">
              Query Editor
            </v-card-title>

            <v-card-text class="pb-0">
              The Query Editor dialog appears when you click either <i>New Query</i> (at the top) or on the <i>Title</i> of the query which appears as a hyperlink in the list.
            </v-card-text>

            <v-card-title class="font=weight-medium pb-1">
              Add a new query
            </v-card-title>

            <v-card-text class="py-0">
              <ul>
                <li>Select the <i>Source</i> of the bulls</li>
                <li>Choose "Custom" or "From Template"
                  <ul>
                    <li>For custom queries, you select all your own criteria</li>
                    <li>Queries from a template are preconfigured (you can customize them after)</li>
                  </ul>
                </li>
                <li>Optionally enter a category</li>
                  <ul>
                    <li><i>Bull Finder</i> automatically groups your queries into the categories you assign them to</li>
                    <li>Any queries that are not assigned a category will be grouped as "Uncategorized" in <i>Bull Finder</i></li>
                  </ul>
                <li>Enter a title (how it will be identified in <i>Bull Finder</i>)</li>
                <li>Optionally enter a description</li>
              </ul>
            </v-card-text>

            <v-row no-gutters>
              <v-col cols="12" xm="4" sm="4" md="4">
                <v-card-text class="pb-0">
                  <v-img
                    :src="bf_img_query_add"
                    style="max-width: 400px"
                  />
                  <span class="caption">Figure showing the New Query dialog for a "Custom" query.</span>
                </v-card-text>
              </v-col>

              <v-col cols="12" xm="4" sm="4" md="4">
                <v-card-text class="pb-0">
                  <v-img
                    :src="bf_img_query_add_from_template"
                    style="max-width: 400px"
                  />
                  <span class="caption">Figure showing the New Query dialog for a query "From Template".</span>
                </v-card-text>
              </v-col>
            </v-row>

            <v-card-title class="font=weight-medium pb-1">
              Edit an existing query
            </v-card-title>

            <v-card-text class="pb-0">
              <v-img
                :src="bf_img_query_edit"
                style="max-width: 400px"
              />
              <span class="caption">When in edit mode, the <i>Source</i> of the bulls cannot be changed.</span>
            </v-card-text>

            <v-divider class="my-5" />

            <v-card-title class="pt-0 pb-0">
              Selecting Your Criteria
            </v-card-title>

            <v-card-text class="pb-0">
              <div class="pb-2">
                Clicking the "Detail" icon (upsidedown triangle beside each query) in <i>Query Manager</i> will open the <i>Query Detail</i> dialog. Select your criteria from the group of dropdown lists under "Desired Traits".  Every query requires at least 1 trait with a maximum of 6. Breed and/or birth year can be changed in this dialog as well.
              </div>
              <v-img
                :src="bf_img_refine_results_criteria"
                style="max-width: 900px"
              />
              <span class="caption">Using the Query Detail dialog you can add/remove bull traits to your query as desired.</span>
            </v-card-text>

            <v-divider class="my-5" />

            <v-card-title class="pt-0">
              Categorizing Your Queries
            </v-card-title>

            <v-card-text class="pb-0">
              <div class="pb-2">If you assign meaningful categories to your queries they will be easier to locate in <i>Bull Finder</i> especially if you have a large number of queries.  An example would be a category for all queries related to performance testing.  Choosing this category in <i>Bull Finder</i> would then display only those queries.</div>
            </v-card-text>

            <v-card-text class="pt-0">
              <v-img
                :src="bf_img_select_category"
                style="max-width: 400px"
              />
              <span class="caption">Figure showing the "Queries Panel" in <i>Bull Finder</i> where only queries in a category called "Performance" are listed.</span>
            </v-card-text>

            <v-divider class="my-5" />

            <v-card-title class="pt-0">
              Fine Tuning Your Query
            </v-card-title>

            <v-card-text class="pb-0">
              <div class="pb-2">Once you have selected the criteria for your query you can then navigate to <i>Bull Finder</i> and view the results.  Here you can fine tune your query by adjusting the individual ranges and values of each trait and setting its priority level. After you save your changes the dialog will close and the query results will automatically refresh.</div>
            </v-card-text>

            <v-card-text class="pb-0">
              <div class="pb-2">Above the query results in <i>Bull Finder</i> there is a toolbar containing a button with a gear for an icon.  Click this button to open the "Settings" dialog.</div>
            </v-card-text>

            <v-card-text class="pt-0">
              <v-img
                :src="bf_img_toolbar_settings"
                style="max-width: 800px"
              />
              <span class="caption">Clicking the "Settngs" button will open the <i>Query Settings</i> dialog which allows you to fine tune your query results.</span>
            </v-card-text>

            <v-card-text class="pt-0">
              <div class="body-2 mb-0">
                Example settings:
              </div>
              <div>1. <i><b>Selection Index (EBV)</b></i> in the top 25%</div>
              <div>2. <i><b>Adj Birth Wt</b></i> between 80 - 90 lbs</div>
              <div>3. <i><b>Hide Colour</b></i> of Black</div>
              <div class="mb-2">4. <i><b>Adj 180 Day Wt</b></i> between 425 - 525 lbs</div>

              <v-img
                :src="bf_img_refine_results_settings"
                style="max-width: 400px"
              />
              <span class="caption">Note: The individual values and ranges can only be modified when the <i>Prioritize</i> toggle is in the off position.</span>
            </v-card-text>

            <v-divider class="my-5" />

            <v-card-title class="headline">
              Prioritizing Traits
            </v-card-title>

            <v-card-text>
              <div>
                When the "Prioritize" toggle is activated you can then prioritize your trait selections by dragging the tiles up or down. The search algorithm treats the first prioritized trait as the most important criterion and moves sequentially down to the last or least important trait.
              </div>
              <div class="my-3">
                Once the traits have been moved into the desired order, clicking the <i>Prioritize</i> toggle button again will disable prioritization and revert to trait edit mode which allows individual values and ranges to be set.
              </div>
              <v-row no-gutters>
                <v-col cols="12" xs="4" sm="4" md="4">
                  <v-img
                    :src="bf_img_refine_results_prioritize_1"
                    style="max-width: 400px"
                    class="mr-2"
                  />
                </v-col>
                <v-col cols="12" xs="4" sm="4" md="4">
                  <v-img
                    :src="bf_img_refine_results_prioritize_2"
                    style="max-width: 400px"
                    class="mr-2"
                  />
                </v-col>
                <v-col cols="12" xs="4" sm="4" md="4">
                  <v-img
                    :src="bf_img_refine_results_prioritize_3"
                    style="max-width: 400px"
                  />
                </v-col>
              </v-row>
              <span class="caption">Sequence showing <i><b>Adj 180 Wt</b></i> being dragged into the lowest position (priority).  As a result <i><b>Hide Colour</b></i> automatically moves up to the 3rd position.</span>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Bull Ranking -->
      <v-expansion-panel>
        <v-expansion-panel-header :class="cls_expansion_title">
          Bull Ranking
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card>
            <v-card-text class="body-1 pt-0">
              <div class="pb-2">
                The left-most column of the results in the <i>Your Matches</i> panel shows the rank each bull has received.  The bulls are ranked according to the priority each trait has been assigned (i.e. bulls with the highest rank will be the ones that meet the criteria for the traits of highest importance).
              </div>
              <v-row no-gutters>
                <v-col cols="12" xs="6" sm="6" md="6">
                  <v-img
                    :src="bf_img_prioritization_rank_col"
                    style="max-width: 600px"
                  />
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-title class="headline pb-0">
              Sequential Match Count
            </v-card-title>

            <v-card-text class="body-1 text-xs-left pb-0">
              To understand how the bulls are ranked we must clarify what is meant by a <i>sequential match count</i>.  This represents the number of traits the bull matched in the order of prioritization.
              <div class="body-1 text-xs-left mt-2 pb-2">
                <i>Example scenario for a bull:</i>
              </div>
            </v-card-text>

            <div class="body-1 text-xs-left ml-3 pb-0">
              <v-icon class="ico-bullet ml-3">
                lens
              </v-icon>Matches the highest prioritized trait
            </div>
            <div class="body-1 text-xs-left ml-3 pb-0">
              <v-icon class="ico-bullet ml-3">
                lens
              </v-icon>Does not match 2nd prioritized trait
            </div>
            <div class="body-1 text-xs-left ml-3 pb-0">
              <v-icon class="ico-bullet ml-3">
                lens
              </v-icon>Matches the 3rd and 4th prioritized traits
            </div>
            <v-card-text class="body-1 pb-0">
              <div>
                In the above scenario, the <i>sequential match count</i> is <b>1</b> because the series of matches was broken when the bull did not meet the 2nd prioritized trait.
                Hence even though the bull matched the 3rd and 4th traits it would be ranked lower than a bull matching <i>only</i> the 1st and 2nd traits since it would have a sequential match count of <b>2</b>.
              </div>
            </v-card-text>

            <v-card-title class="title pb-3">
              Colour Codes
            </v-card-title>

            <div class="body-1 ml-3 pb-0">
              The following colour-code descriptions assume that the total number of prioritized traits is <b>4</b>.
            </div>
            <div class="body-1 ml-3 pb-0">
              A <i>non-primary</i> trait is any trait below the one given the highest priority.
            </div>
            <div class="body-1 ml-3 pb-0">
              Recall that a <i>sequential match count</i> is the number of traits the bull matched in the order of prioritization.
            </div>

            <v-card-text class="body-1 pb-4">
              <div>
                <v-icon class="ico-rank perfect-match">
                  lens
                </v-icon>Has a <i>sequential match count</i> of <b>4</b> (matches all criteria)
              </div>
              <div>
                <v-icon class="ico-rank average-match">
                  lens
                </v-icon>Has a <i>sequential match count</i> of <b>3</b> (out of 4)
              </div>
              <div>
                <v-icon class="ico-rank poor-match">
                  lens
                </v-icon>Has a <i>sequential match count</i> of <b>1 - 2</b> (out of 4)
              </div>
              <div>
                <v-icon class="ico-rank not-a-match">
                  lens
                </v-icon>Has a <i>sequential match count</i> of <b>0</b> but matches 1 or more of the 3 <i>non-primary</i> traits
              </div>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Other Features -->
      <v-expansion-panel>
        <v-expansion-panel-header :class="cls_expansion_title">
          Other Features
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card>
            <!-- <v-card-text class="body-1 text-xs-left pb-0 pt-0">
              <div class="body-1 text-xs-left ml-3 pb-0">
                <v-icon class="ico-bullet">
                  lens
                </v-icon>Bulls can be sorted by any column by clicking the up/down arrow in the column header
              </div>
              <div class="body-1 text-xs-left ml-3 pb-0">
                <v-icon class="ico-bullet">
                  lens
                </v-icon>Results can be shown in pages of 5, 10, 25 or ALL by clicking the “Rows per page” drop-down menu (bottom)
              </div>
              <div class="body-1 text-xs-left ml-3 pb-0">
                <v-icon class="ico-bullet">
                  lens
                </v-icon>Favorites can be selected/unselected by clicking the heart icon
              </div>
              <div class="body-1 text-xs-left ml-3 pb-0">
                <v-icon class="ico-bullet">
                  lens
                </v-icon>Bull details can be viewed in a popup window by clicking the colour-coded button under the “Rank” column
              </div>
              <div class="body-1 text-xs-left ml-5 pb-0">
                <v-icon class="ico-bullet-inner">
                  lens
                </v-icon>Popup shows the bull’s value for each trait and <v-icon
                  small
                  color="green"
                >
                  done
                </v-icon> or <v-icon
                  small
                  color="red"
                >
                  clear
                </v-icon> to indicate whether the bull matched (or did not match) the desired value or range
              </div>
              <div class="body-1 text-xs-left ml-3 pb-0">
                <v-icon class="ico-bullet">
                  lens
                </v-icon>The results can be refreshed by clicking the “Refresh” button
              </div>
              <div class="body-1 text-xs-left ml-5">
                <v-icon class="ico-bullet-inner">
                  lens
                </v-icon>This is only applicable during sale day activities when bull status/availability changes rapidly
              </div>
              <div class="body-1 text-xs-left ml-3 pb-0">
                <v-icon class="ico-bullet">
                  lens
                </v-icon>The top 20 bulls can be exported as an Excel spreadsheet
              </div>
              <div class="body-1 text-xs-left ml-5">
                <v-icon class="ico-bullet-inner">
                  lens
                </v-icon>Contains more detail as numbered values are presented in full (not rounded off)
              </div>
              <div class="body-1 text-xs-left ml-5 pb-3">
                <v-icon class="ico-bullet-inner">
                  lens
                </v-icon>Cells are colour-coded to indicate matching/mismatching trait values for each bull
              </div>
              <v-img
                :src="bf_img_other_features"
                style="max-width: 600px"
              />
              <div class="caption text-xs-left pb-1">
                Figure showing the <i>Query Results</i> panel with a page size of 10
              </div>
            </v-card-text> -->

            <v-card-title class="title pb-4 pt-0">
              Favorites
            </v-card-title>
            <v-card-text class="body-1 text-xs-left pb-0">
              To mark a bull as a favorite, click the heart-shaped icon (far right).  The grey, circular label containing a number indicates how many other people have also selected this bull as a favorite.
            </v-card-text>
            <v-card-title class="title pb-0">
              File Export
            </v-card-title>
            <v-card-text class="body-1 pb-0">
              <div class="body-1 text-xs-left pb-3">
                To export the top 20 bulls as an Excel spreadsheet, click the “download” button in the top right corner of the Your Matches panel (black arrow pointing down).
              </div>
              <v-img
                :src="bf_img_export"
                style="max-width: 200px"
              />
              <div class="caption text-xs-left pb-0">
                Figure showing the Export download options <i>Bull Finder</i>
              </div>
            </v-card-text>
            <v-card-title class="headline pb-0">
              Mobile Friendly
            </v-card-title>
            <v-card-text class="body-1">
              <div class="body-1 text-xs-left pb-0">
                When using a mobile device the panels will automatically adapt to your screen size.  The <i>Criteria Detail</i> panel can be collapsed and allow the bull search results to appear at the top.
              </div>
              <v-row no-gutters>
                <v-col cols="12" xs="3" sm="3" md="3">
                  <v-img
                    :src="bf_img_mobile_results_portrait"
                    style="max-width: 300px"
                  />
                </v-col>
                <v-col cols="12" xs="3" sm="3" md="3">
                  <v-img
                    :src="bf_img_mobile_results_collapsed_queries"
                    style="max-width: 300px"
                  />
                </v-col>
                <v-col cols="12" xs="3" sm="3" md="3">
                  <v-img
                    :src="bf_img_mobile_results_landscape"
                    style="max-width: 600px"
                  />
                </v-col>
              </v-row>
              <div class="caption text-xs-left pb-0">
                Figure showing various methods of viewing <i>Bull Finder</i> on a mobile device
              </div>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>

    </v-expansion-panels>
  </v-container>
</template>

<script>
import AppPageTitle from '@/components/AppPageTitle'
import { AppColor } from '@/config.js'

export default {
  name: 'Help',
  components: {
    AppPageTitle
  },
  data () {
    return {
      appColor_Background: AppColor.Background,
      bullfinderImagePrefix: 'https://cattlytics.blob.core.windows.net/assets/screenshots/',
      panel: null
    }
  },

  computed: {
    birthYear () {
      var today = new Date()
      return today.getFullYear() - 1
    },

    ca_img_signin () {
     return this.bullfinderImagePrefix + 'Cattlytics Sign In.png'
    },
    ca_img_signup () {
     return this.bullfinderImagePrefix + 'Cattlytics Sign Up.png'
    },
    ca_img_login () {
     return this.bullfinderImagePrefix + 'Cattlytics Log In.png'
    },
    ca_img_password () {
     return this.bullfinderImagePrefix + 'Cattlytics Password Rules.png'
    },

    bf_img_home_1 () {
      return this.bullfinderImagePrefix + 'Home - With Main Menu Arrow.png'
    },
    bf_img_home_2 () {
      return this.bullfinderImagePrefix + 'Home - With Main Menu.png'
    },
    bf_img_home_3 () {
      return this.bullfinderImagePrefix + 'Home - Sale Day Mode.png'
    },
    bf_img_home_launch_bullfinder () {
      return this.bullfinderImagePrefix + 'Home - Launch Bull Finder .png'
    },

    bf_img_query_1 () {
      return this.bullfinderImagePrefix + 'Bull Finder Home.png'
    },
    bf_img_query_panels () {
      return this.bullfinderImagePrefix + 'Bull Finder - Panel Names.png'
    },
    bf_img_query_panels_maternal () {
      return this.bullfinderImagePrefix + 'Bull Finder - Query - Maternal.png'
    },
    bf_img_query_card () {
      return this.bullfinderImagePrefix + 'Bull Finder Query Card.png'
    },
    bf_img_results_toolbar () {
      return this.bullfinderImagePrefix + 'Bull Finder Results Panel Toolbar.png'
    },

    // Understanding 
    bf_img_query_understanding_1 () {
      return this.bullfinderImagePrefix + 'Bull Finder Query - Traits.png'
    },
    bf_img_query_result () {
      return this.bullfinderImagePrefix + 'Bull Finder Query - Results.png'
    },
    bf_img_query_result_green () {
      return this.bullfinderImagePrefix + 'Bull Finder Query - Rank Green.png'
    },
    bf_img_query_result_yellow () {
      return this.bullfinderImagePrefix + 'Bull Finder Query - Rank Yellow.png'
    },
    bf_img_query_result_red () {
      return this.bullfinderImagePrefix + 'Bull Finder Query - Rank Red.png'
    },
    bf_img_query_result_gray () {
      return this.bullfinderImagePrefix + 'Bull Finder Query - Rank Gray.png'
    },

    // refining query
    bf_img_refine_results_criteria () {
      return this.bullfinderImagePrefix + 'Bull Finder - Refine Results - Criteria.png'
    },
    bf_img_refine_results_settings () {
      return this.bullfinderImagePrefix + 'Bull Finder - Refine Results - Settings.png'
    },
    bf_img_refine_results_prioritize_1 () {
      return this.bullfinderImagePrefix + 'Bull Finder - Refine Results - Prioritize 1.png'
    },
    bf_img_refine_results_prioritize_2 () {
      return this.bullfinderImagePrefix + 'Bull Finder - Refine Results - Prioritize 2.png'
    },
    bf_img_refine_results_prioritize_3 () {
      return this.bullfinderImagePrefix + 'Bull Finder - Refine Results - Prioritize 3.png'
    },

    // prioritization
    bf_img_prioritization_rank_col () {
      return this.bullfinderImagePrefix + 'Bull Finder - Prioritization - Rank Column.png'
    },

    // other features
    bf_img_other_features () {
      return this.bullfinderImagePrefix + 'Bull Finder - Other Features.png'
    },

    // mobile
    bf_img_mobile_results_portrait () {
      return this.bullfinderImagePrefix + 'Bull Finder - Mobile - Results Portrait.png'
    },
    bf_img_mobile_results_collapsed_queries () {
      return this.bullfinderImagePrefix + 'Bull Finder - Mobile - Results Collapsed Queries.png'
    },
    bf_img_mobile_results_landscape () {
      return this.bullfinderImagePrefix + 'Bull Finder - Mobile - Results Landscape.png'
    },

    // export
    bf_img_export () {
      return this.bullfinderImagePrefix + 'Bull Finder - Export.png'
    },

    // others
    bf_img_bb_ranch_popup () {
      return this.bullfinderImagePrefix + 'Bull Finder - BB Ranch Popup.png'
    },
    bf_img_bull_detail_popup () {
      return this.bullfinderImagePrefix + 'Bull Finder - Bull Detail Popup.png'
    },

    // manage queries
    bf_img_manage_queries_main_menu () {
      return this.bullfinderImagePrefix + 'Bull Finder - Manage Queries - Main Menu.png'
    },
    bf_img_manage_queries_button () {
      return this.bullfinderImagePrefix + 'Bull Finder - Manage Queries - Button.png'
    },
    bf_img_manager () {
      return this.bullfinderImagePrefix + 'Bull Finder - Manager.png'
    },
    bf_img_query_add () {
      return this.bullfinderImagePrefix + 'Bull Finder - Query Add.png'
    },
    bf_img_query_add_from_template () {
      return this.bullfinderImagePrefix + 'Bull Finder - Query Add From Template.png'
    },
    bf_img_query_edit () {
      return this.bullfinderImagePrefix + 'Bull Finder - Query Edit.png'
    },
    bf_img_select_category () {
      return this.bullfinderImagePrefix + 'Bull Finder - Select Category.png'
    },
    bf_img_toolbar_settings () {
      return this.bullfinderImagePrefix + 'Bull Finder - Toolbar - Settings.png'
    },

    // classes
    cls_expansion_title () {
      return 'title font-weight-regular'
    }
  }
}
</script>

<style scoped>

.ico-bullet {
  font-size: 8px !important;
  margin: 0 10px 5px 0;
  color: #333;
}

.ico-bullet-inner {
  font-size: 8px !important;
  margin: 0 10px 5px 0;
}

.ico-rank {
  font-size: 12px !important;
  margin: 0 10px 5px 0;
  color: #333;
}

.perfect-match {
  color: #43a047 !important;
}

.average-match {
  color: #fdd835 !important;
}

.poor-match {
  color: #ff5252 !important;
}

.not-a-match {
  color: #777 !important;
}
</style>
